/**
 * Frontend React Components for NameDrop Audio plugin
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "@wordpress/element";
import { render, createRoot } from "@wordpress/element";

// Local SVG icons to replace Iconify CDN dependency
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const LoadingIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ animation: "spin 1s linear infinite" }}
  >
    <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
  </svg>
);

const VolumeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const VolumeDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
  </svg>
);

const VolumeUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

// Add CSS for loading animation
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

// Theme configurations
const THEMES = {
  dark: {
    background: "#1a1a1a",
    buttonBg: "#ffffff",
    buttonColor: "#1a1a1a",
    waveColor: "#888888",
    progressColor: "#ffffff",
  },
  light: {
    background: "#ffffff",
    buttonBg: "#1a1a1a",
    buttonColor: "#ffffff",
    waveColor: "#333333",
    progressColor: "#1976d2",
  },
};

// Get system theme preference
function getSystemTheme() {
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

// Resolve theme based on preference
function resolveTheme(themePreference) {
  if (themePreference === "auto") {
    return getSystemTheme();
  }
  return themePreference;
}

// Waveform data for visualization
const WAVEFORM_DATA = [
  0.3, 0.7, 0.9, 1.0, 0.4, 0.8, 0.6, 0.5, 0.9, 0.7, 0.8, 0.4, 0.6, 0.9, 0.5,
  0.7, 0.8, 0.3, 0.6, 0.9, 0.4, 0.7, 0.5, 0.8, 0.6, 0.9, 0.3, 0.7, 0.4, 0.8,
];

const NAMEDROP_API_BASE =
  window?.namedropAudio?.apiBase || "https://api.namedrop.io";

/**
 * Extract username from various formats
 * @param {string} input - Username or NameDrop URL
 * @returns {string|null} - Extracted username or null if invalid
 */
/**
 * Extract username or brand from various formats
 * @param {string} input - Username, Brand URL, or NameDrop URL
 * @returns {object|null} - Extracted data { type: 'user'|'brand', value: string } or null
 */
function extractUsername(input) {
  if (!input || typeof input !== "string") {
    console.log(
      "NameDrop Audio: Invalid input for extraction:",
      input,
    );
    return null;
  }

  const trimmed = input.trim();
  console.log("NameDrop Audio: Extracting from:", trimmed);

  // Look for direct audio links
  const audioMatch = trimmed.match(
    /(?:https?:\/\/)?(?:data\.)?(?:namedrop\.io|nmdrp\.xyz)\/audio\/(.+?)(?:\.mp3)?$/i,
  );
  if (audioMatch) {
    console.log(
      "NameDrop Audio: Extracted direct audio URL:",
      trimmed,
    );
    return { type: "audio", value: trimmed };
  }

  // Check for brand URL pattern first: namedrop.io/brands/BRAND_ID
  // Made protocol optional and allowing www. or not, case insensitive
  const brandMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?namedrop\.io\/brands\/([a-zA-Z0-9_-]+)/i,
  );
  if (brandMatch) {
    console.log(
      "NameDrop Audio: Extracted brand from URL:",
      brandMatch[1],
    );
    return { type: "brand", value: brandMatch[1] };
  }

  // Check if it's a nmdrp.xyz URL
  const nmdripUrlMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?nmdrp\.xyz\/([a-zA-Z0-9_-]+)/i,
  );
  if (nmdripUrlMatch) {
    console.log(
      "NameDrop Audio: Extracted username from nmdrp.xyz URL:",
      nmdripUrlMatch[1],
    );
    return { type: "user", value: nmdripUrlMatch[1] };
  }

  // Check if it's a namedrop.io URL (standard profile)
  // CRITICAL FIX: Negative lookahead to ensure we don't match 'brands' or 'audio' as a username
  const namedropUrlMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?namedrop\.io\/(?!brands\/|audio\/)([a-zA-Z0-9_-]+)/i,
  );
  if (namedropUrlMatch) {
    console.log(
      "NameDrop Audio: Extracted username from namedrop.io URL:",
      namedropUrlMatch[1],
    );
    return { type: "user", value: namedropUrlMatch[1] };
  }

  // Check if it's just a username (no spaces, valid characters)
  if (/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    console.log("NameDrop Audio: Using direct username:", trimmed);
    return { type: "user", value: trimmed };
  }

  console.log("NameDrop Audio: No valid username or brand found in:", trimmed);
  return null;
}

/**
 * Fetch audio URL from NameDrop API
 * @param {object|string} entity - Username string (legacy) or entity object {type, value}
 * @returns {Promise<string>} - Promise resolving to audio URL
 */
async function fetchAudioUrl(entity) {
  try {
    // Handle backward compatibility where entity is just a string
    const target = typeof entity === 'string' 
      ? { type: 'user', value: entity }
      : entity;

    if (!target || !target.value) {
      throw new Error("Invalid entity provided to fetchAudioUrl");
    }

    if (target.type === "audio") {
      console.log("NameDrop Audio: Returning direct audio URL:", target.value);
      return target.value; /* Bypass API entirely */
    }

    const cleanValue = String(target.value).trim();
    console.log(`NameDrop Audio: Fetching audio for ${target.type}:`, cleanValue);

    const baseUrl = NAMEDROP_API_BASE;
    let endpoint, params;

    if (target.type === 'brand') {
      endpoint = "/brands";
      params = `?brand_username=${encodeURIComponent(cleanValue)}&source=brandpage`;
    } else {
      endpoint = "/profile";
      params = `?username=${encodeURIComponent(cleanValue)}`;
    }

    const apiUrl = baseUrl + endpoint + params;
    console.log("NameDrop Audio: Constructed API URL:", apiUrl);

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    console.log("NameDrop Audio: API response:", data);

    let recordingUrl = null;

    if (target.type === 'brand') {
      // Brand API returns a single object or array
      if (Array.isArray(data) && data.length > 0) {
        recordingUrl = data[0].recording || data[0].audioUrl;
      } else if (data && typeof data === 'object') {
        recordingUrl = data.recording || data.audioUrl;
      }
    } else {
      // Profile API returns object with recording field
      if (data.recording) {
        recordingUrl = data.recording;
      }
    }

    if (!recordingUrl) {
      throw new Error(`No recording found for this ${target.type}`);
    }

    console.log("NameDrop Audio: Found recording URL:", recordingUrl);
    return recordingUrl;
  } catch (error) {
    console.error("NameDrop Audio: API fetch failed:", error);
    throw new Error(`Failed to fetch audio: ${error.message}`);
  }
}

/**
 * Custom hook for audio player functionality
 */
function useAudioPlayer(audioElement, audioSource) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [volume, setVolume] = useState(0.8); // Default volume at 80%
  const animationRef = useRef(null);

  // Fetch audio URL if audioSource is provided instead of direct audio element
  useEffect(() => {
    if (!audioSource || audioElement) return; // Skip if we already have audio element

    console.log(
      "NameDrop Audio: Starting audio source fetch for:",
      audioSource,
    );

    const username = extractUsername(audioSource);
    if (!username) {
      console.error(
        "NameDrop Audio: Invalid username or NameDrop URL:",
        audioSource,
      );
      setError("Invalid username or NameDrop URL");
      return;
    }

    setIsLoading(true);
    setError(null);

    console.log("NameDrop Audio: Fetching audio for username:", username);

    fetchAudioUrl(username)
      .then((url) => {
        console.log("NameDrop Audio: Successfully fetched audio URL:", url);
        setAudioUrl(url);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("NameDrop Audio: Failed to fetch audio URL:", err);
        setError(err.message);
        setIsLoading(false);
      });
  }, [audioSource, audioElement]);

  const pauseAllOtherAudio = useCallback(() => {
    document.querySelectorAll("audio").forEach((audio) => {
      if (audio !== audioElement && !audio.paused) {
        audio.pause();
      }
    });
  }, [audioElement]);

  const play = useCallback(async () => {
    let targetAudio = audioElement;

    // If we have a fetched URL but no audio element, create one
    if (!targetAudio && audioUrl) {
      targetAudio = new Audio(audioUrl);

      // Add event listeners for dynamically created audio elements
      const handleEnded = () => {
        console.log("NameDrop Audio: Dynamic audio playback ended");
        setIsPlaying(false);
      };

      const handlePause = () => {
        console.log("NameDrop Audio: Dynamic audio paused");
        setIsPlaying(false);
      };

      const handleError = (e) => {
        console.error("NameDrop Audio: Dynamic audio loading error:", e);
        setError("Error loading audio file");
        setIsPlaying(false);
      };

      targetAudio.addEventListener("ended", handleEnded);
      targetAudio.addEventListener("pause", handlePause);
      targetAudio.addEventListener("error", handleError);
    }

    if (!targetAudio) return;

    // Set volume
    targetAudio.volume = volume;

    try {
      pauseAllOtherAudio();
      await targetAudio.play();
      setIsPlaying(true);
      setError(null);
      console.log("NameDrop Audio: Audio started successfully");
    } catch (err) {
      console.error("NameDrop Audio: Audio playback failed:", err);
      setError("Audio playback failed. Click to retry.");
    }
  }, [audioElement, audioUrl, pauseAllOtherAudio, volume]);

  const pause = useCallback(() => {
    let targetAudio = audioElement;

    // If we have a fetched URL but no audio element, we might have created one in play()
    if (!targetAudio && audioUrl) {
      // Find any audio element playing our URL
      const allAudio = document.querySelectorAll("audio");
      targetAudio = Array.from(allAudio).find(
        (audio) => audio.src === audioUrl && !audio.paused,
      );
    }

    if (!targetAudio) return;
    targetAudio.pause();
    setIsPlaying(false);
  }, [audioElement, audioUrl]);

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  useEffect(() => {
    if (!audioElement) return;

    const handleEnded = () => {
      console.log("NameDrop Audio: Audio playback ended");
      setIsPlaying(false);
    };

    const handlePause = () => {
      console.log("NameDrop Audio: Audio paused");
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error("NameDrop Audio: Audio loading error:", e);
      setError("Error loading audio file");
      setIsPlaying(false);
    };

    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("error", handleError);

    // Set initial volume
    audioElement.volume = volume;

    // Ensure audio is loaded
    if (audioElement.readyState === 0) {
      audioElement.load();
    }

    return () => {
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("error", handleError);
    };
  }, [audioElement, volume]);

  return {
    isPlaying,
    isLoading,
    error,
    play,
    pause,
    toggle,
    animationRef,
    audioUrl,
    volume,
    setVolume,
  };
}

/**
 * Play/Pause Button Component
 */
function PlayButton({
  isPlaying,
  onClick,
  theme,
  disabled = false,
  isLoading = false,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const buttonStyle = {
    background: theme.buttonBg,
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled || isLoading ? "not-allowed" : "pointer",
    color: theme.buttonColor,
    padding: "0",
    transition: "transform 0.1s ease, box-shadow 0.1s ease",
    transform:
      isHovered && !disabled && !isLoading ? "scale(1.05)" : "scale(1)",
    boxShadow:
      isHovered && !disabled && !isLoading
        ? "0 2px 8px rgba(0,0,0,0.2)"
        : "none",
    opacity: disabled ? 0.6 : 1,
    flexShrink: 0,
  };

  // Show loading icon while fetching
  // Get the appropriate icon component
  const getIconComponent = () => {
    if (isLoading) return <LoadingIcon />;
    if (isPlaying) return <PauseIcon />;
    return <PlayIcon />;
  };

  return (
    <button
      style={buttonStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled || isLoading}
    >
      <div
        style={{
          color: theme.buttonColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {getIconComponent()}
      </div>
    </button>
  );
}

/**
 * Waveform Display Component
 */
function WaveformDisplay({ isPlaying, theme, animationRef }) {
  const [currentBar, setCurrentBar] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = setInterval(() => {
        setCurrentBar((prev) => (prev + 1) % WAVEFORM_DATA.length);
      }, 100);
    } else {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      setCurrentBar(0);
    }

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, animationRef]);

  const containerStyle = {
    flex: 1,
    height: "24px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "1px",
    minWidth: "100px",
    maxWidth: "140px",
    overflow: "hidden",
    margin: "0 4px",
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      {WAVEFORM_DATA.map((height, index) => {
        const distance = Math.abs(index - currentBar);
        const isActive = isPlaying && distance <= 2;
        const opacity = isActive
          ? distance === 0
            ? 1
            : distance === 1
            ? 0.7
            : 0.4
          : 0.8;

        let backgroundColor = theme.waveColor;
        if (isActive) {
          const r = parseInt(theme.progressColor.slice(1, 3), 16);
          const g = parseInt(theme.progressColor.slice(3, 5), 16);
          const b = parseInt(theme.progressColor.slice(5, 7), 16);
          backgroundColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }

        return (
          <div
            key={index}
            style={{
              width: "2px",
              height: `${height * 24}px`,
              background: backgroundColor,
              borderRadius: "1px",
              transition: "background 0.2s ease",
              opacity: isActive ? opacity : 0.8,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Volume Control Component
 */
function VolumeControl({ volume, onVolumeChange, theme }) {
  const [isHovered, setIsHovered] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const hideTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
    setShowSlider(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Delay hiding to allow moving to the slider
    hideTimeoutRef.current = setTimeout(() => {
      setShowSlider(false);
    }, 150);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    onVolumeChange(newVolume);
  };

  const getVolumeIconComponent = () => {
    if (volume === 0) return <VolumeOffIcon />;
    if (volume < 0.5) return <VolumeDownIcon />;
    return <VolumeUpIcon />;
  };

  const buttonStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    position: "relative",
  };

  const sliderContainerStyle = {
    position: "absolute",
    bottom: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    marginBottom: "8px",
    background: theme.background,
    padding: "8px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    display: showSlider ? "block" : "none",
    zIndex: 10,
  };

  const sliderStyle = {
    width: "60px",
    height: "3px",
    background: theme.waveColor,
    borderRadius: "2px",
    outline: "none",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
  };

  return (
    <div
      style={{
        ...buttonStyle,
        position: "relative",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          color: theme.background === "#1a1a1a" ? "#ffffff" : "#1a1a1a",
          opacity: isHovered ? 1 : 0.7,
          transition: "opacity 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {getVolumeIconComponent()}
      </div>
      <div
        style={sliderContainerStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={volume}
          onChange={handleVolumeChange}
          style={sliderStyle}
        />
        <style>{`
          input[type="range"]::-webkit-slider-thumb {
            appearance: none;
            width: 12px;
            height: 12px;
            background: ${theme.progressColor};
            border-radius: 50%;
            cursor: pointer;
          }
          input[type="range"]::-moz-range-thumb {
            width: 12px;
            height: 12px;
            background: ${theme.progressColor};
            border-radius: 50%;
            cursor: pointer;
            border: none;
          }
        `}</style>
      </div>
    </div>
  );
}

/**
 * Error Message Component
 */
function ErrorMessage({ message, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const errorStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 0, 0, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    color: "#ff0000",
    borderRadius: "12px",
    zIndex: 1,
  };

  return <div style={errorStyle}>{message}</div>;
}

/**
 * Audio Tooltip Component
 */
function AudioTooltip({
  audioElement,
  audioSource,
  theme: initialTheme,
  themePreference = "auto",
}) {
  const {
    isPlaying,
    isLoading,
    error,
    toggle,
    animationRef,
    volume,
    setVolume,
  } = useAudioPlayer(audioElement, audioSource);
  const [showError, setShowError] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Resolve the initial theme
    return resolveTheme(themePreference);
  });

  // Update theme when system theme changes (only if preference is auto)
  useEffect(() => {
    if (themePreference === "auto") {
      const updateTheme = () => {
        setCurrentTheme(resolveTheme("auto"));
      };

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => updateTheme();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // If not auto, just use the preference directly
      setCurrentTheme(themePreference);
    }
  }, [themePreference]);

  // Get the actual theme object
  const theme = THEMES[currentTheme] || THEMES.light;

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // Extract username for profile link
  const usernameData = audioSource ? extractUsername(audioSource) : null;
  
  let profileUrl = null;
  let displayUsername = "";
  
  if (usernameData) {
    if (usernameData.type === "user") {
      // Determine base domain based on original audioSource
      const baseDomain = typeof audioSource === "string" && audioSource.includes("nmdrp.xyz") 
        ? "nmdrp.xyz" 
        : "namedrop.io";
      profileUrl = `https://${baseDomain}/${usernameData.value}`;
      displayUsername = usernameData.value;
    } else if (usernameData.type === "brand") {
      profileUrl = `https://namedrop.io/brands/${usernameData.value}`;
      displayUsername = usernameData.value;
    }
    // direct audio types do not generate a profileUrl
  }

  const containerStyle = {
    background: theme.background,
    borderRadius: "20px",
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: "240px",
    maxWidth: "320px",
    width: "fit-content",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    position: "relative",
  };

  const topRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    height: "40px",
    justifyContent: "space-between",
  };

  const handlePlayPause = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  const handleProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (profileUrl) {
      window.open(profileUrl, "_blank");
    }
  };

  const poweredByStyle = {
    fontSize: "9px",
    color: theme.background === "#1a1a1a" ? "#888888" : "#666666",
    textAlign: "center",
    marginTop: "2px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  };

  return (
    <div style={containerStyle}>
      <div style={topRowStyle}>
        <PlayButton
          isPlaying={isPlaying}
          isLoading={isLoading}
          onClick={handlePlayPause}
          theme={theme}
          disabled={!!error}
        />
        <WaveformDisplay
          isPlaying={isPlaying}
          theme={theme}
          animationRef={animationRef}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <VolumeControl
            volume={volume}
            onVolumeChange={setVolume}
            theme={theme}
          />
          {profileUrl && (
            <button
              onClick={handleProfileClick}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.8,
                transition: "opacity 0.2s ease",
                borderRadius: "4px",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = "1")}
              onMouseLeave={(e) => (e.target.style.opacity = "0.8")}
              title={`Visit ${displayUsername}'s profile`}
            >
              <div
                style={{
                  color: theme.background === "#1a1a1a" ? "#ffffff" : "#1a1a1a",
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ExternalLinkIcon />
              </div>
            </button>
          )}
        </div>
      </div>
      <div style={poweredByStyle}>
        powered by{" "}
        <img
          src={`${window.namedropAudio?.pluginUrl || ""}/assets/logos/${
            theme.background === "#1a1a1a"
              ? "namedrop_logo_full_white.png"
              : "namedrop_logo_full_grey.png"
          }`}
          alt="NameDrop"
          style={{
            height: "12px",
            width: "auto",
            verticalAlign: "middle",
            marginLeft: "3px",
          }}
        />
      </div>
      {showError && error && (
        <ErrorMessage message={error} onDismiss={() => setShowError(false)} />
      )}
    </div>
  );
}

// Export AudioTooltip for use in the block editor
export { AudioTooltip };

/**
 * Main React App Component
 */
function NameDropAudioApp() {
  useEffect(() => {
    console.log("NameDrop Audio: React app initialized");

    // Initialize other non-hover audio functionality
    initializeAudioPlayers();
    initializeInlineAudio();
    initializeSimpleAudioLinks();
    initializeHoverAudio();
  }, []);

  return null; // This component just initializes the app
}

/**
 * Initialize hover audio elements with React components
 */
function initializeHoverAudio() {
  const hoverElements = document.querySelectorAll(".namedrop-hover-text");
  console.log(
    "NameDrop Audio: Found " + hoverElements.length + " hover audio elements",
  );

  if (hoverElements.length === 0) return;

  hoverElements.forEach((element) => {
    // Get data attributes
    const audioUrl = element.getAttribute("data-audio-url");
    const audioSource = element.getAttribute("data-audio-source"); // New: username or NameDrop URL
    const position = element.getAttribute("data-position") || "top";
    const themeStyle = element.getAttribute("data-style") || "dark";
    const autoplay = element.getAttribute("data-autoplay") === "true";

    console.log("NameDrop Audio: Processing hover element with attributes:", {
      audioUrl,
      audioSource,
      position,
      themeStyle,
      autoplay,
    });

    // Find tooltip and audio element
    const tooltip = element.querySelector(".namedrop-hover-tooltip");
    const audioElement = element.querySelector("audio");

    if (!tooltip || (!audioElement && !audioSource && !audioUrl)) {
      console.log(
        "NameDrop Audio: Skipping element - missing tooltip or audio source",
      );
      return;
    }

    // Get theme configuration
    const themePreference = themeStyle || "auto";
    const resolvedTheme = resolveTheme(themePreference);
    const theme = THEMES[resolvedTheme] || THEMES.light;

    // Render React component into tooltip
    // Priority: audioElement > audioSource > audioUrl
    if (audioElement) {
      try {
        render(
          <AudioTooltip
            audioElement={audioElement}
            theme={theme}
            themePreference={themePreference}
          />,
          tooltip,
        );
      } catch (error) {
        console.error("NameDrop Audio: React render error:", error);
        // Fallback to vanilla JS if React fails
        tooltip.innerHTML = "<div>Audio player loading...</div>";
      }
    } else if (audioSource) {
      try {
        render(
          <AudioTooltip
            audioSource={audioSource}
            theme={theme}
            themePreference={themePreference}
          />,
          tooltip,
        );
      } catch (error) {
        console.error("NameDrop Audio: React render error:", error);
        // Fallback to vanilla JS if React fails
        tooltip.innerHTML = "<div>Loading audio...</div>";
      }
    } else if (audioUrl) {
      try {
        // Create a temporary audio element for backward compatibility
        const tempAudio = document.createElement("audio");
        tempAudio.src = audioUrl;
        tempAudio.style.display = "none";
        element.appendChild(tempAudio);
        render(
          <AudioTooltip
            audioElement={tempAudio}
            theme={theme}
            themePreference={themePreference}
          />,
          tooltip,
        );
      } catch (error) {
        console.error("NameDrop Audio: React render error:", error);
        // Fallback to vanilla JS if React fails
        tooltip.innerHTML = "<div>Audio player loading...</div>";
      }
    }

    // Set up hover behavior for autoplay
    if (autoplay) {
      element.addEventListener("mouseenter", () => {
        // Pause all other audio first
        document.querySelectorAll("audio").forEach((audio) => {
          if (audio !== audioElement) audio.pause();
        });

        // Try to play this audio
        audioElement.play().catch((error) => {
          console.warn(
            "NameDrop Audio: Autoplay failed (browser policy):",
            error,
          );
        });
      });

      // Pause when mouse leaves
      element.addEventListener("mouseleave", () => {
        if (!audioElement.paused) {
          audioElement.pause();
        }
      });
    }

    // Set up mobile touch behavior (show tooltip on touch)
    if ("ontouchstart" in window) {
      element.addEventListener("click", (e) => {
        if (tooltip) {
          // Toggle tooltip visibility for touch devices
          if (tooltip.style.visibility === "visible") {
            tooltip.style.visibility = "hidden";
            tooltip.style.opacity = "0";
          } else {
            tooltip.style.visibility = "visible";
            tooltip.style.opacity = "1";

            // Hide all other tooltips
            document
              .querySelectorAll(".namedrop-hover-tooltip")
              .forEach((tip) => {
                if (tip !== tooltip) {
                  tip.style.visibility = "hidden";
                  tip.style.opacity = "0";
                }
              });

            e.preventDefault();
            e.stopPropagation();
          }
        }
      });
    }
  });

  // Add document click listener to close tooltips on mobile
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".namedrop-hover-text")) {
      document
        .querySelectorAll(".namedrop-hover-tooltip")
        .forEach((tooltip) => {
          tooltip.style.visibility = "";
          tooltip.style.opacity = "";
        });
    }
  });
}

// Legacy vanilla JS functions (non-hover audio functionality)
function initializeAudioPlayers() {
  const audioPlayers = document.querySelectorAll(".namedrop-audio-player");

  audioPlayers.forEach(function (player) {
    const audioElement = player.querySelector("audio");
    if (!audioElement) return;

    // Add loading state
    audioElement.addEventListener("loadstart", function () {
      player.classList.add("loading");
    });

    // Remove loading state when ready
    audioElement.addEventListener("loadeddata", function () {
      player.classList.remove("loading");
    });

    // Handle errors
    audioElement.addEventListener("error", function (e) {
      player.classList.remove("loading");
      player.classList.add("error");

      const errorMsg = document.createElement("div");
      errorMsg.className = "namedrop-audio-error-message";
      errorMsg.textContent = "Error loading audio file";
      player.appendChild(errorMsg);
    });

    // Add play/pause events
    audioElement.addEventListener("play", function () {
      player.classList.add("playing");
    });

    audioElement.addEventListener("pause", function () {
      player.classList.remove("playing");
    });

    audioElement.addEventListener("ended", function () {
      player.classList.remove("playing");
    });
  });
}

function initializeInlineAudio() {
  console.log("NameDrop Audio: Looking for inline audio elements...");

  const selectors = [
    ".namedrop-inline-audio",
    "[data-audio-url]",
    "span[data-audio-url]",
    "*[data-audio-url]",
    ".namedrop-inline-audio-wrapper",
    ".namedrop-audio-wrapper",
    "audio",
  ];

  let inlineAudioElements = [];

  selectors.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    console.log(
      `NameDrop Audio: Selector "${selector}" found ${elements.length} elements`,
    );
    elements.forEach((el) => {
      if (!inlineAudioElements.includes(el)) {
        inlineAudioElements.push(el);
      }
    });
  });

  console.log(
    "NameDrop Audio: Total unique inline audio elements found:",
    inlineAudioElements.length,
  );

  inlineAudioElements.forEach(function (element, index) {
    console.log(`NameDrop Audio: Processing element ${index}:`, element);

    if (element.dataset.audioProcessed) {
      console.log("NameDrop Audio: Element already processed, skipping");
      return;
    }

    const audioUrl = element.dataset.audioUrl;
    console.log("NameDrop Audio: Audio URL:", audioUrl);

    if (!audioUrl) {
      console.log("NameDrop Audio: No audio URL found, skipping");
      return;
    }

    let audioElement = element.querySelector("audio");
    if (!audioElement) {
      console.log("NameDrop Audio: Creating audio element...");
      audioElement = createInlineAudioElement(element, audioUrl);
      if (!audioElement) {
        console.log("NameDrop Audio: Failed to create audio element");
        return;
      }
      console.log("NameDrop Audio: Audio element created successfully");
    } else {
      console.log("NameDrop Audio: Audio element already exists");
    }

    element.dataset.audioProcessed = "true";

    // Make inline audio more interactive
    element.addEventListener("click", function (e) {
      console.log("NameDrop Audio: Element clicked");
      if (e.target.tagName.toLowerCase() === "audio") return;

      if (audioElement.paused) {
        console.log("NameDrop Audio: Playing audio");
        audioElement.play().catch(function (error) {
          console.warn("NameDrop Audio: Audio playback failed:", error);
        });
      } else {
        console.log("NameDrop Audio: Pausing audio");
        audioElement.pause();
      }
    });

    // Visual feedback
    audioElement.addEventListener("play", function () {
      console.log("NameDrop Audio: Audio started playing");
      element.classList.add("playing");
    });

    audioElement.addEventListener("pause", function () {
      console.log("NameDrop Audio: Audio paused");
      element.classList.remove("playing");
    });

    audioElement.addEventListener("ended", function () {
      console.log("NameDrop Audio: Audio ended");
      element.classList.remove("playing");
    });

    if (!element.title) {
      element.title = "Click to play/pause audio";
    }
  });
}

function createInlineAudioElement(element, audioUrl) {
  console.log(
    "NameDrop Audio: Creating inline audio element for URL:",
    audioUrl,
  );

  const label = element.dataset.label || "";
  const autoPlay = element.dataset.autoplay === "true";
  const showControls = element.dataset.showControls !== "false";

  console.log(
    "NameDrop Audio: Settings - label:",
    label,
    "autoPlay:",
    autoPlay,
    "showControls:",
    showControls,
  );

  const playerContainer = document.createElement("span");
  playerContainer.className = "namedrop-inline-audio-player";
  playerContainer.style.cssText =
    "display: inline-block; margin-left: 4px; vertical-align: middle;";

  if (label) {
    const labelElement = document.createElement("span");
    labelElement.className = "namedrop-inline-audio-label";
    labelElement.textContent = label;
    labelElement.style.cssText =
      "font-size: 0.85em; color: #666; margin-right: 4px; font-weight: 500;";
    playerContainer.appendChild(labelElement);
  }

  const audioElement = document.createElement("audio");
  audioElement.src = audioUrl;
  audioElement.controls = showControls;
  audioElement.preload = "metadata";
  audioElement.className = "namedrop-inline-audio-element";
  audioElement.style.cssText =
    "height: 24px; width: 120px; vertical-align: middle; border-radius: 3px;";

  if (autoPlay) {
    audioElement.autoplay = true;
  }

  console.log("NameDrop Audio: Audio element created:", audioElement);

  audioElement.addEventListener("error", function (e) {
    console.error("NameDrop Audio: Error loading audio:", e);
    playerContainer.innerHTML =
      '<span style="color: red; font-size: 0.8em;">Error loading audio</span>';
  });

  audioElement.addEventListener("loadedmetadata", function () {
    console.log("NameDrop Audio: Audio metadata loaded successfully");
  });

  playerContainer.appendChild(audioElement);
  element.appendChild(playerContainer);

  console.log("NameDrop Audio: Player container added to element");

  return audioElement;
}

function initializeSimpleAudioLinks() {
  const audioLinks = document.querySelectorAll(
    'a.namedrop-simple-audio-link[data-audio="true"]',
  );

  audioLinks.forEach(function (link) {
    link.addEventListener("click", function (e) {
      e.preventDefault();

      const audioUrl = link.href;
      if (!audioUrl) return;

      let audioPlayer = link.nextElementSibling;
      if (
        !audioPlayer ||
        !audioPlayer.classList.contains("namedrop-inline-player")
      ) {
        audioPlayer = createInlinePlayer(audioUrl);
        link.parentNode.insertBefore(audioPlayer, link.nextSibling);
      }

      if (audioPlayer.style.display === "none" || !audioPlayer.style.display) {
        audioPlayer.style.display = "inline-block";
        const audio = audioPlayer.querySelector("audio");
        if (audio) {
          audio.play().catch((error) => {
            console.error("Audio playback failed:", error);
          });
        }
      } else {
        audioPlayer.style.display = "none";
        const audio = audioPlayer.querySelector("audio");
        if (audio) {
          audio.pause();
        }
      }
    });
  });
}

// Initialize the React app when DOM is ready
function initializeNameDropAudio() {
  try {
    // Create a container for the React app
    const appContainer = document.createElement("div");
    appContainer.id = "namedrop-audio-app";
    appContainer.style.display = "none";
    document.body.appendChild(appContainer);

    // Render the main React app
    render(<NameDropAudioApp />, appContainer);
    console.log("NameDrop Audio: React app initialized successfully");
  } catch (error) {
    console.error("NameDrop Audio: Failed to initialize React app:", error);
    // Fallback to vanilla JS initialization
    initializeAudioPlayers();
    initializeInlineAudio();
    initializeSimpleAudioLinks();
    initializeHoverAudio();
  }
}

document.addEventListener("DOMContentLoaded", initializeNameDropAudio);

// Also initialize immediately if DOM is already loaded
if (document.readyState === "loading") {
  // DOM is still loading, wait for DOMContentLoaded
} else {
  // DOM is already loaded, initialize immediately
  initializeNameDropAudio();
}
